import { useParams, Link } from 'react-router-dom'
import {
    useGetDevice,
    useUpdateDevice,
    useSendDeviceCommand,
} from '../../../api/routes/devices'
import Header from './header'
import './styles.css'
import {
    CopyAll,
    Warning,
    RestartAlt,
    SystemUpdateAlt,
} from '@mui/icons-material'
import Tooltip from './tooltip'
import { useState, useCallback } from 'react'
import { useSocketRoom } from '../../../hooks/useSocketRoom'
import { authClient, hasPermission } from '../../../lib/auth-client'
import { Switch, TextField } from '@mui/material'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../components/ui/dialog'
import { Button } from '../../../components/ui/button'
import { Edit, Check, Close } from '@mui/icons-material'
import RecordLogs from '../../../components/shared/record-logs'
import SoftwareVersions from './software-versions'
import BusinessEditor from './business-editor'
import { useGetDeviceSettings } from '../../../api/routes/device-settings'
import { useBusinessModuleCheck } from '../../../api/routes/modules'
import { useGetServiceSchedules } from '../../../api/routes/services'

const SingleDeviceRoute = () => {
    const { id: deviceId } = useParams()
    const { data: device, isLoading } = useGetDevice(deviceId as string)
    const { data: session, isPending: sessionLoading } = authClient.useSession()
    const user = session?.user as any
    const updateDevice = useUpdateDevice()
    const sendCommand = useSendDeviceCommand()
    const { data: moduleAccess } = useBusinessModuleCheck(
        device?.business?.id || null,
        'device-config'
    )
    const hasDeviceConfig = !!moduleAccess?.hasAccess
    const { data: deviceSettings } = useGetDeviceSettings(
        hasDeviceConfig ? (deviceId as string) : ''
    )
    const { data: serviceModuleAccess } = useBusinessModuleCheck(
        device?.business?.id || null,
        'service'
    )
    const hasServiceModule = !!serviceModuleAccess?.hasAccess
    const { data: serviceSchedules } = useGetServiceSchedules(
        hasServiceModule ? (deviceId as string) : ''
    )

    const [copied, setCopied] = useState(false)
    const [showWarningModal, setShowWarningModal] = useState(false)
    const [isEditingName, setIsEditingName] = useState(false)
    const [editedName, setEditedName] = useState('')
    const [commandConfirm, setCommandConfirm] = useState<string | null>(null)
    const [deviceStatus, setDeviceStatus] = useState<string>('offline')
    const [commandSent, setCommandSent] = useState(false)
    const [commandResult, setCommandResult] = useState<{
        command: string
        status: string
        message: string
    } | null>(null)

    const isOnline = deviceStatus === 'online'

    useSocketRoom(`device:${deviceId}`, {
        events: {
            currentState: useCallback((data: any) => {
                const status = data?.values?.status?.value
                if (status) setDeviceStatus(status)
            }, []),
            message: useCallback((data: any) => {
                if (data?.key === 'status') {
                    setDeviceStatus(data.value)
                } else if (data?.key === 'commandResult') {
                    setCommandResult(data.value)
                }
            }, []),
        },
    })

    if (isLoading || sessionLoading || !user) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">Device not found</p>
                </div>
            </div>
        )
    }

    const handleCopyId = () => {
        navigator.clipboard.writeText(device?.deviceId || device?.id)
        setCopied(true)

        setTimeout(() => {
            setCopied(false)
        }, 2000)
    }

    const handleActiveToggle = (checked: boolean) => {
        if (!checked) {
            setShowWarningModal(true)
            return
        }
        updateDevice.mutate({
            id: deviceId as string,
            data: { cloudActive: true },
        })
    }

    const handleOverrideToggle = (checked: boolean) => {
        updateDevice.mutate({
            id: deviceId as string,
            data: { cloudActiveOverride: checked },
        })
    }

    const handleConfirmDisable = () => {
        updateDevice.mutate({
            id: deviceId as string,
            data: { cloudActive: false },
        })
        setShowWarningModal(false)
    }

    const handleCancelDisable = () => {
        setShowWarningModal(false)
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header
                deviceId={deviceId}
                name={device?.name}
                businessId={device?.business?.id}
            />

            {/* Device Information */}
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Device Information
                    </h3>
                </div>
                <div className="p-6">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                        {hasPermission(user.role, {
                            device: ['configure'],
                        }) && (
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Device ID
                                </dt>
                                <dd className="text-sm text-foreground flex items-center gap-1.5 font-mono">
                                    {device?.deviceId || device?.id}
                                    <Tooltip
                                        value="Copy Device ID"
                                        pressedValue={copied ? 'Copied!' : null}
                                    >
                                        <CopyAll
                                            className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                                            style={{ fontSize: 16 }}
                                            onClick={handleCopyId}
                                        />
                                    </Tooltip>
                                </dd>
                            </div>
                        )}
                        <div className="space-y-1">
                            <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Name
                            </dt>
                            <dd className="text-sm text-foreground flex items-center gap-2">
                                {isEditingName ? (
                                    <div className="flex items-center gap-2 flex-1">
                                        <TextField
                                            value={editedName}
                                            onChange={(e) =>
                                                setEditedName(e.target.value)
                                            }
                                            size="small"
                                            variant="outlined"
                                            className="flex-1"
                                        />
                                        <button
                                            className="p-1 rounded-md hover:bg-accent text-green-600 transition-colors"
                                            onClick={() => {
                                                updateDevice.mutate({
                                                    id: deviceId as string,
                                                    data: { name: editedName },
                                                })
                                                setIsEditingName(false)
                                            }}
                                        >
                                            <Check style={{ fontSize: 18 }} />
                                        </button>
                                        <button
                                            className="p-1 rounded-md hover:bg-accent text-muted-foreground transition-colors"
                                            onClick={() => {
                                                setIsEditingName(false)
                                                setEditedName(
                                                    device?.name || ''
                                                )
                                            }}
                                        >
                                            <Close style={{ fontSize: 18 }} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span>{device?.name}</span>
                                        {hasPermission(user.role, {
                                            device: ['update'],
                                        }) && (
                                            <button
                                                className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                                onClick={() => {
                                                    setEditedName(
                                                        device?.name || ''
                                                    )
                                                    setIsEditingName(true)
                                                }}
                                            >
                                                <Edit
                                                    style={{ fontSize: 14 }}
                                                />
                                            </button>
                                        )}
                                    </>
                                )}
                            </dd>
                        </div>
                        {hasPermission(user.role, {
                            device: ['update'],
                        }) && (
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Serial Number
                                </dt>
                                <dd className="text-sm text-foreground font-mono">
                                    {device?.serial}
                                </dd>
                            </div>
                        )}
                        {hasPermission(user.role, {
                            device: ['update'],
                            business: ['read'],
                        }) ? (
                            <BusinessEditor
                                deviceId={deviceId}
                                business={device?.business}
                            />
                        ) : (
                            <div className="space-y-1">
                                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Business
                                </dt>
                                <dd className="text-sm text-foreground">
                                    {device?.business?.name}
                                </dd>
                            </div>
                        )}
                        <div className="space-y-1">
                            <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Model
                            </dt>
                            <dd className="text-sm text-foreground">
                                {device?.model?.name}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Config Settings Summary */}
            {hasDeviceConfig &&
                deviceSettings &&
                deviceSettings.length > 0 &&
                deviceSettings.some((s: any) => s.value != null) && (
                    <div className="rounded-lg border bg-card mb-6">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                                Settings
                            </h3>
                            <Link
                                to={`/devices/${deviceId}/settings`}
                                className="text-xs text-primary hover:text-primary/80 transition-colors"
                            >
                                View All
                            </Link>
                        </div>
                        <div className="px-6 py-4">
                            <div className="flex flex-wrap gap-3">
                                {deviceSettings
                                    .filter((s: any) => s.value != null)
                                    .slice(0, 4)
                                    .map((s: any) => {
                                        let displayValue = String(s.value)
                                        if (s.dataType === 'boolean') {
                                            const opts = s.options || {}
                                            displayValue =
                                                s.value === 'true'
                                                    ? opts.trueLabel || 'Yes'
                                                    : opts.falseLabel || 'No'
                                        }
                                        return (
                                            <span
                                                key={s.templateId}
                                                className="inline-flex items-center gap-1.5 rounded-md border bg-muted/50 px-2.5 py-1 text-xs"
                                            >
                                                <span className="text-muted-foreground">
                                                    {s.label || s.name}:
                                                </span>
                                                <span className="font-medium text-foreground">
                                                    {displayValue}
                                                </span>
                                            </span>
                                        )
                                    })}
                            </div>
                        </div>
                    </div>
                )}

            {/* Service Schedule Summary */}
            {hasServiceModule &&
                serviceSchedules &&
                serviceSchedules.length > 0 &&
                (() => {
                    const overdueCount = serviceSchedules.filter(
                        (s: any) => s.status === 'overdue'
                    ).length
                    const dueSoonCount = serviceSchedules.filter(
                        (s: any) => s.status === 'due_soon'
                    ).length
                    return overdueCount > 0 || dueSoonCount > 0 ? (
                        <div className="rounded-lg border bg-card mb-6">
                            <div className="px-6 py-4 border-b flex items-center justify-between">
                                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                                    Service Schedule
                                </h3>
                                <Link
                                    to={`/devices/${deviceId}/services`}
                                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                                >
                                    View All
                                </Link>
                            </div>
                            <div className="px-6 py-4">
                                <div className="flex flex-wrap gap-3">
                                    {overdueCount > 0 && (
                                        <span className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                                            {overdueCount} Overdue
                                        </span>
                                    )}
                                    {dueSoonCount > 0 && (
                                        <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                                            {dueSoonCount} Due Soon
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null
                })()}

            {/* Device Configuration */}
            {hasPermission(user.role, { device: ['update'] }) && (
                <div className="rounded-lg border bg-card mb-6">
                    <div className="px-6 py-4 border-b">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                            Configuration
                        </h3>
                    </div>
                    <div className="divide-y divide-border">
                        <div className="flex items-center justify-between px-6 py-4">
                            <div className="space-y-0.5">
                                <p className="text-sm font-medium text-foreground">
                                    Active
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Apply a cloud license to the device
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {device?.cloudActiveOverride && (
                                    <span className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                        Cloud Override
                                    </span>
                                )}
                                <Switch
                                    checked={device?.cloudActive || false}
                                    onChange={(e) =>
                                        handleActiveToggle(e.target.checked)
                                    }
                                    disabled={updateDevice.isPending}
                                    size="small"
                                />
                            </div>
                        </div>

                        {hasPermission(user.role, {
                            device: ['configure'],
                        }) && (
                            <div className="flex items-center justify-between px-6 py-4">
                                <div className="space-y-0.5">
                                    <p className="text-sm font-medium text-foreground">
                                        Override Active
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Bypass device activation without
                                        assigning a license.
                                    </p>
                                </div>
                                <Switch
                                    checked={
                                        device?.cloudActiveOverride || false
                                    }
                                    onChange={(e) =>
                                        handleOverrideToggle(e.target.checked)
                                    }
                                    disabled={updateDevice.isPending}
                                    size="small"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Device Controls */}
            {hasPermission(user.role, { device: ['configure'] }) && (
                <div className="rounded-lg border bg-card mb-6">
                    <div className="px-6 py-4 border-b flex items-center justify-between">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                            Device Controls
                        </h3>
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                {isOnline && (
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                )}
                                <span
                                    className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isOnline ? 'bg-green-500' : 'bg-red-400'}`}
                                />
                            </span>
                            <span
                                className={`text-xs font-medium ${isOnline ? 'text-green-700' : 'text-muted-foreground'}`}
                            >
                                {isOnline ? 'Online' : 'Offline'}
                            </span>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    setCommandConfirm('restart-runtime')
                                }
                                disabled={!isOnline || sendCommand.isPending}
                                className="gap-2"
                            >
                                <RestartAlt style={{ fontSize: 16 }} />
                                Restart Runtime
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCommandConfirm('update')}
                                disabled={!isOnline || sendCommand.isPending}
                                className="gap-2"
                            >
                                <SystemUpdateAlt style={{ fontSize: 16 }} />
                                Request Update
                            </Button>
                        </div>
                        {!isOnline && (
                            <p className="text-xs text-muted-foreground mt-3">
                                Device must be online to send commands.
                            </p>
                        )}
                        {commandSent && (
                            <p className="text-xs text-green-600 mt-3 font-medium">
                                Command sent successfully.
                            </p>
                        )}
                        {sendCommand.isError && (
                            <p className="text-xs text-destructive mt-3 font-medium">
                                Failed to send command.
                            </p>
                        )}
                        {commandResult && (
                            <div
                                className={`mt-4 rounded-md border p-3 ${commandResult.status === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                            >
                                <p
                                    className={`text-xs font-semibold ${commandResult.status === 'success' ? 'text-green-800' : 'text-red-800'}`}
                                >
                                    {commandResult.command === 'restart-runtime'
                                        ? 'Restart Runtime'
                                        : 'Update'}
                                    : {commandResult.status}
                                </p>
                                {commandResult.message && (
                                    <p
                                        className={`text-xs mt-1 ${commandResult.status === 'success' ? 'text-green-700' : 'text-red-700'}`}
                                    >
                                        {commandResult.message}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {hasPermission(user.role, { device: ['configure'] }) && (
                <SoftwareVersions deviceId={deviceId as string} />
            )}

            {hasPermission(user.role, { log: ['read'] }) && (
                <RecordLogs
                    resourceType="device"
                    resourceId={deviceId}
                    title="Device Activity"
                />
            )}

            {/* Warning Modal for Disabling Device */}
            <Dialog open={showWarningModal} onOpenChange={handleCancelDisable}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning className="text-amber-500" />
                            Disable Device
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground mb-4">
                            Are you sure you want to disable this device? This
                            action will:
                        </p>
                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 mb-4">
                            <li>
                                Stop the device from sending data to the cloud
                            </li>
                            <li>Prevent access to live data</li>
                            <li>Device data will be removed in 30 days</li>
                        </ul>
                        <p className="text-xs text-muted-foreground">
                            You can re-enable the device at any time.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancelDisable}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDisable}
                            disabled={updateDevice.isPending}
                        >
                            {updateDevice.isPending
                                ? 'Disabling...'
                                : 'Disable Device'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm Command Dialog */}
            <Dialog
                open={commandConfirm !== null}
                onOpenChange={() => setCommandConfirm(null)}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning className="text-amber-500" />
                            {commandConfirm === 'restart-runtime'
                                ? 'Restart Runtime'
                                : 'Request Update'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            {commandConfirm === 'restart-runtime'
                                ? 'Are you sure you want to restart the runtime on this device? This will temporarily interrupt device operations.'
                                : 'Are you sure you want to request a software update on this device?'}
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setCommandConfirm(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (commandConfirm) {
                                    setCommandResult(null)
                                    sendCommand.mutate({
                                        id: deviceId as string,
                                        command: commandConfirm,
                                    })
                                    setCommandSent(true)
                                    setTimeout(
                                        () => setCommandSent(false),
                                        2000
                                    )
                                }
                                setCommandConfirm(null)
                            }}
                            disabled={sendCommand.isPending}
                        >
                            {sendCommand.isPending ? 'Sending...' : 'Confirm'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export { SingleDeviceRoute }
